import { useCallback } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { getPagePreloadConfig, type PagePreloadKey } from './registry';

/**
 * Hook to prefetch page status data when the browser is idle.
 *
 * Uses requestIdleCallback to ensure prefetching doesn't interfere with
 * current page load. Skips prefetching if already cached or if the browser
 * doesn't support requestIdleCallback.
 *
 * @example
 * ```tsx
 * const { prefetch } = usePrefetchPage();
 * prefetch('ipPools');
 * ```
 */
export default function usePrefetchPage() {
	const queryClient = useQueryClient();

	const prefetch = useCallback(
		(key: PagePreloadKey) => {
			const config = getPagePreloadConfig(key);
			if (!config) return;

			// Skip if already in cache
			const cached = queryClient.getQueryData(config.queryKey);
			if (cached) return;

			// Only prefetch in browsers that support requestIdleCallback
			// This ensures true deprioritization; older browsers skip prefetching
			if (!window.requestIdleCallback) return;

			window.requestIdleCallback(() => {
				queryClient.fetchQuery({
					queryKey: config.queryKey,
					queryFn: config.queryFn,
					staleTime: 5 * 60 * 1000, // 5 minutes
				});
			});
		},
		[queryClient],
	);

	return { prefetch };
}
